import React from "react"
import { GetServerSideProps } from "next"
import { InstantSearch } from "react-instantsearch-dom"
import { findResultsState } from "react-instantsearch-dom/server"
import { InstantSearchProps, SearchState } from "react-instantsearch-core"

import SEO from "@secureo/common/components/SEO"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"
import VirtualRefinements from "@secureo/common/components/Algolia/VirtualRefinements"
import AlgoliaSearchUI from "components/Algolia/AlgoliaSearchUI"

import useAlgoliaURLSync, { urlToSearchState } from "@secureo/common/hooks/useAlgoliaURLSync"

import normalizeLocale from "@secureo/common/utils/normalizeLocale"

import searchClient from "@secureo/common/utils/algolia/searchClient"
import { lockingSystemFilters } from "config/filterConfig"
import { currentPath as ancestorPath } from "./index"

import { CustomAppProps } from "pages/_app"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import serializeJSON from "@secureo/common/utils/serializeJSON"

const name = "Mechanische Schließanlagen"
const metaDescription =
	"Mechanische Schließanlagen der neusten Generation bieten einen Einbruchschutz auf höchstem Niveau. Verfügbare Hersteller: ✓ EVVA ✓ ABUS ✓ ASSA ABLOY"

const currentPath: BreadCrumb = {
	label: name,
	href: `/locking-system/mechanic`,
}

const MechanicLockingSystemsPage = (props: Props & InstantSearchProps & CustomAppProps) => {
	const {
		locale,
		indexName,
		resultsState,
		initialSearchState = {},
		// eslint-disable-next-line
		onSearchParameters = () => {},
		virtualRefinements,
		widgetsCollector,
		isFindResultsStateRenderMode = false,
	} = props

	const asPath = `/${locale}${currentPath.href}`
	const [searchState, onSearchStateChange] = useAlgoliaURLSync(
		initialSearchState,
		`/[locale]${currentPath.href}`,
		asPath,
	)

	return (
		<>
			<SEO
				asPath={asPath}
				pageType="lockingSystemDetail"
				categoryName={name}
				title={name}
				appendLocalizedShopNameWithPipe
				description={metaDescription}
			/>
			<InstantSearch
				indexName={indexName}
				searchClient={searchClient}
				resultsState={resultsState}
				searchState={searchState}
				onSearchParameters={onSearchParameters}
				onSearchStateChange={onSearchStateChange}
				widgetsCollector={widgetsCollector}
			>
				<VirtualRefinements virtualRefinements={virtualRefinements} />
				{!isFindResultsStateRenderMode && (
					<BreadCrumbs
						locale={locale}
						currentPath={currentPath}
						ancestors={[ancestorPath]}
					/>
				)}
				<AlgoliaSearchUI
					locale={locale}
					indexName={indexName}
					filters={lockingSystemFilters}
					pageType="locking-systems"
					title={name}
					shortDescription={""}
					description={""}
					virtualRefinements={virtualRefinements}
				/>
			</InstantSearch>
		</>
	)
}

interface PreviewProps {
	language: string
	locale: string
	countryCode: CountryCode

	// Algolia
	indexName: string
	initialSearchState: SearchState
	virtualRefinements: SearchState
}

interface Props extends PreviewProps {
	resultsState: InstantSearchProps["resultsState"]
	/**
	 * I'm sorry
	 */
	widgetsCollector?: any
	isFindResultsStateRenderMode?: boolean
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { req, res, params } = context

	const rawLocale = params?.locale as string
	const { locale, language, countryCode } = normalizeLocale(rawLocale)

	const indexName = `DZN-${locale}-locking-systems`
	const searchState = urlToSearchState(req.url)

	const previewProps: PreviewProps = {
		language,
		locale,
		countryCode,

		// Algolia
		indexName,
		initialSearchState: searchState,
		virtualRefinements: {
			refinementList: {
				lockingSystemType: ["mechanic"],
			},
		},
	}

	let resultsState: InstantSearchProps["resultsState"] = {}
	try {
		resultsState = await findResultsState(
			(props: any) => (
				<MechanicLockingSystemsPage
					{...previewProps}
					{...props}
					isFindResultsStateRenderMode
				/>
			),
			{
				indexName,
				searchClient,
				searchState,
			},
		)
	} catch (err) {
		console.error(err)
		res.statusCode = 500
		return {
			props: {
				statusCode: 500,
				error: "Failed to create resultsState",
			},
		}
	}
	const serializedResultsState = serializeJSON(resultsState)

	const props: Props = {
		...previewProps,
		resultsState: serializedResultsState,
	}

	return {
		props,
	}
}

export default MechanicLockingSystemsPage
